import React, { useEffect, useState } from 'react';
import { ClearAllIcon, FormControlSpacing, FeatureFlag } from 'element';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Typography,
  FormHelperText,
} from 'extended-oxygen-elements';
import {
  useActions,
  useSelectorWithProps,
  useSetCheckbox,
  useSetFormField,
  useUpdateStatusEffect,
} from 'hooks';
import {
  DefaultBreakRules,
  EditDefaultBreakRules,
  EnableBreaksCustomisationCheckbox,
  HelperTextWrapper,
} from 'page/Settings/components';
import {
  BOX_ACCOUNT_TIMESHEET_APPLY_CUSTOMISED_BREAKS,
  BOX_ACCOUNT_TIMESHEET_DEFAULT_BREAKS_RESET,
  getCustomBreaksLoading,
  getCustomBreaksStatus,
  getDefaultBreaksLoading,
} from 'state/Account';
import { DefaultBreakItem, FieldGroupProps } from 'type';
import DefaultBreaksFromRadioGroup from './DefaultBreaksFromRadioGroup';
import {
  DefaultBreaksFieldGroupErrors,
  DefaultBreaksFieldGroupValue,
} from './types';
import { useDispatch, useSelector } from 'react-redux';
import nanoid from 'nanoid';
import { CustomiseBreaksForm } from '../../../components/CustomiseBreaksForm';
import defaultBreaksFieldGroupValidator from './defaultBreaksFieldGroupValidator';

type Props = FieldGroupProps<
  DefaultBreaksFieldGroupValue,
  DefaultBreaksFieldGroupErrors
> & { isDisabled: boolean };

export default DefaultBreaksFieldGroup;

export function DefaultBreaksFieldGroup({
  values,
  onChange,
  errors,
  isDisabled,
}: Props) {
  const dispatch = useDispatch();

  const [customiseBreaks, setCustomiseBreaks] = useState(false);
  const [defaultBreaks, setDefaultBreaks] = useState<any[]>(
    values.default_breaks_rules
  );
  const [editErrors, setEditErrors] = useState(errors.default_breaks_rules);

  const isLoading = useSelectorWithProps(getDefaultBreaksLoading);
  const isApplyingCustomBreaks = useSelector(getCustomBreaksLoading);

  const { makeSetFormField } = useSetFormField(onChange);
  const { makeSetCheckbox } = useSetCheckbox(onChange);

  useEffect(() => {
    if (isDisabled || !values.auto_breaks) {
      setCustomiseBreaks(false);
    }
  }, [isDisabled, values.auto_breaks]);

  useUpdateStatusEffect(getCustomBreaksStatus, {
    success: () => {
      customiseBreaks && setCustomiseBreaks(false);
    },
  });

  const resetBreaks = useActions(
    BOX_ACCOUNT_TIMESHEET_DEFAULT_BREAKS_RESET.request
  );

  const handleResetToDefaultsBtnClick = () => {
    const { auto_breaks, breaks_from_shift, breaks_from_rules } = values;
    resetBreaks({ auto_breaks, breaks_from_shift, breaks_from_rules });
  };

  const handleChangeBreakDuration = (value: any) => {
    setDefaultBreaks(value);
  };

  const applyBreaks = () => {
    const e = defaultBreaksFieldGroupValidator({
      auto_breaks: true,
      breaks_from_rules: true,
      breaks_from_shift: false,
      default_breaks_rules: defaultBreaks,
    });

    setEditErrors(e.default_breaks_rules);

    if (typeof e.default_breaks_rules === 'undefined') {
      const payload: DefaultBreakItem[] = defaultBreaks.map((b) => ({
        break_duration: b.break_duration,
        shift_duration: b.shift_duration,
        condition: b.condition,
      }));

      dispatch(BOX_ACCOUNT_TIMESHEET_APPLY_CUSTOMISED_BREAKS.request(payload));
    }
  };

  const pushBreakIntoSettings = (breakItem: DefaultBreakItem) => {
    setDefaultBreaks((prevState) =>
      [
        ...prevState,
        {
          ...breakItem,
          condition: '<=',
          id: nanoid(),
        },
      ].sort((a, b) => a.shift_duration - b.shift_duration)
    );
  };

  const handleCustomiseBreaks = () => {
    const breaks = values.default_breaks_rules.filter(
      (b) => b.condition !== '>'
    );
    setDefaultBreaks(breaks);
    setCustomiseBreaks(!customiseBreaks);
  };

  return (
    <>
      <Typography variant="h4" component="h2">
        Set up your breaks
      </Typography>

      <FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={values.auto_breaks}
              onChange={makeSetCheckbox('auto_breaks')}
            />
          }
          label="Enable breaks"
        />
        <HelperTextWrapper>
          <FormHelperText fontSize="medium">
            Use Breathe’s default breaks.
          </FormHelperText>
        </HelperTextWrapper>
      </FormControl>

      <DefaultBreaksFromRadioGroup
        values={values}
        onChange={onChange}
        disabled={isLoading || !values.auto_breaks}
      />

      <FeatureFlag name={'customiseBreaks'}>
        <FeatureFlag.On>
          <FormControl>
            <EnableBreaksCustomisationCheckbox
              handleCustomiseBreaks={handleCustomiseBreaks}
              isEnabled={values.auto_breaks && !isDisabled}
              modeEnabled={customiseBreaks}
            />
          </FormControl>
        </FeatureFlag.On>
      </FeatureFlag>

      {!customiseBreaks && (
        <Box maxWidth={440}>
          <DefaultBreakRules
            value={values.default_breaks_rules}
            onChange={makeSetFormField('default_breaks_rules')}
            errors={errors.default_breaks_rules}
            disabled={
              isLoading || !values.auto_breaks || !values.breaks_from_rules
            }
          />
        </Box>
      )}

      <FeatureFlag name={'customiseBreaks'}>
        <FeatureFlag.On>
          {customiseBreaks && !isDisabled && (
            <>
              <Box sx={{ '@media (min-width: 600px)': { maxWidth: '440px' } }}>
                <EditDefaultBreakRules
                  value={defaultBreaks}
                  onChange={handleChangeBreakDuration}
                  errors={editErrors}
                />
              </Box>

              <Box sx={{ '@media (min-width: 600px)': { maxWidth: '440px' } }}>
                <CustomiseBreaksForm
                  applyBreaks={applyBreaks}
                  handleCustomiseBreaks={handleCustomiseBreaks}
                  customiseBreaks={customiseBreaks}
                  defaultBreaks={defaultBreaks}
                  pushBreakIntoSettings={pushBreakIntoSettings}
                  isLoading={isApplyingCustomBreaks}
                />
              </Box>
            </>
          )}
        </FeatureFlag.On>
      </FeatureFlag>

      <FormControlSpacing>
        {!customiseBreaks && (
          <Button
            startIcon={<ClearAllIcon />}
            loading={isLoading}
            onClick={handleResetToDefaultsBtnClick}
            disabled={!values.auto_breaks || isDisabled}
          >
            Reset to default
          </Button>
        )}
      </FormControlSpacing>
    </>
  );
}
